import {
  YES_NO,
  applicantSection,
  feeProofAttachment,
  otherAttachments,
  parcelsField,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import { proxyFeeAttachmentWhen } from "modules/ebiForms/definitions/sharedHousing";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * A12. Przekształcenie prawa użytkowania wieczystego w prawo własności (grunty gminy; dla gruntów Skarbu Państwa —
 * starosta). Najpierw rodzaj gruntu (wariant wniosku w BE), zgodnie z „Uwagami dla EBI” katalogu:
 * - grunt mieszkaniowy — przekształcony z mocy prawa (ustawa z 20.07.2018 r., Dz.U. 2018 poz. 1716 ze zm.): wniosek
 *   o zaświadczenie (art. 4; 50 zł opłaty skarbowej, tryb 30-dniowy po uzasadnieniu) i/lub zgłoszenie zamiaru wniesienia
 *   opłaty jednorazowej (art. 7 ust. 7) z wnioskiem o bonifikatę (uchwała rady gminy); układ jak wzór Bierunia
 *   (https://bip.bierun.pl/download/1.1-Wniosek-o-wydanie-zaswiadczenia-o-przeksztalceniu-2019-wersja-ostat-2-01-2019,13104.pdf,
 *   pobrany 22.10.2026: nieruchomość, udział związany z lokalem, imiona rodziców i obywatelstwo, załączniki);
 * - inny grunt — sprzedaż użytkownikowi wieczystemu na zasadach ogólnych (art. 32, art. 37 ust. 2 pkt 5, art. 69–69a
 *   ugn); roszczenie z art. 198g ugn wygasło 31.08.2024 r. Oświadczenia o sposobie zabudowy i działalności gospodarczej
 *   jak we wzorze Powiatu Świdnickiego dla art. 198g (https://www.powiatswidnik.pl/images/wydzialy/geodezja/pliki/wniosek_o_sprzedaz_prawa_wlasnosci_na_rzecz_uzytkownika_wieczystego_w_trybie_art.198g_ugn.pdf,
 *   pobrany 22.10.2026); wzoru Warszawy BM-12-01 nie pobierano.
 * Ścieżka ustawy z 29.07.2005 r. (przekształcenie decyzją, po wyroku TK K 29/13 tylko dla użytkowników wieczystych
 * z 13.10.2005 r.) pominięta jak w „Uwagach dla EBI” — do potwierdzenia z wydziałem.
 */
const RESIDENTIAL: EbiCondition = { field: "rodzajGruntu", equals: "mieszkaniowy" };
const SALE: EbiCondition = { field: "rodzajGruntu", equals: "inny" };
const CERTIFICATE: EbiCondition = { all: [RESIDENTIAL, { field: "zakresSprawy", includes: "zaswiadczenie" }] };
const ONE_OFF_FEE: EbiCondition = { all: [RESIDENTIAL, { field: "zakresSprawy", includes: "oplataJednorazowa" }] };
const URGENT: EbiCondition = { all: [CERTIFICATE, { field: "tryb30Dni", equals: true }] };
const DISCOUNT: EbiCondition = { all: [ONE_OFF_FEE, { field: "bonifikata", equals: "tak" }] };
const SHARE_IN_LAND: EbiCondition = { all: [RESIDENTIAL, { field: "udzialLokal", equals: true }] };
const CO_OWNERS: EbiCondition = { field: "wspoluzytkownicy", equals: "tak" };

const [PROXY_DOCUMENT] = proxyAttachments();

const A12: IEbiFormDefinition = {
  code: "A12",
  version: 1,
  title: "Wniosek w sprawie przekształcenia prawa użytkowania wieczystego w prawo własności",
  category: EEbiFormCategory.Property,
  office: EEbiFormOffice.Municipality,
  description:
    "Zaświadczenie o przekształceniu i opłata jednorazowa (grunty mieszkaniowe) albo sprzedaż gruntu użytkownikowi wieczystemu (pozostałe grunty).",
  keywords: ["użytkowanie wieczyste", "przekształcenie", "zaświadczenie", "opłata przekształceniowa", "opłata jednorazowa", "bonifikata", "sprzedaż gruntu"],
  organ: "Wójt / Burmistrz / Prezydent Miasta (grunty gminy)",
  legalBasis:
    "art. 4 i art. 7 ustawy z dnia 20 lipca 2018 r. o przekształceniu prawa użytkowania wieczystego gruntów zabudowanych na cele mieszkaniowe w prawo własności tych gruntów; art. 32, art. 37 ust. 2 pkt 5 i art. 69–69a ustawy z dnia 21 sierpnia 1997 r. o gospodarce nieruchomościami",
  request: "Wnoszę o załatwienie sprawy dotyczącej prawa użytkowania wieczystego nieruchomości opisanej we wniosku, w zakresie wskazanym poniżej.",
  feeLabel: "do 50 zł",
  fee: "Zaświadczenie o przekształceniu — 50 zł opłaty skarbowej; pełnomocnictwo — 17 zł. Wniosek o sprzedaż gruntu (ustawa o gospodarce nieruchomościami) — bez opłaty skarbowej, także od pełnomocnictwa; nabywca ponosi koszty wyceny, umowy notarialnej i wpisu w księdze wieczystej.",
  deadlineLabel: "do 4 miesięcy",
  deadline:
    "Zaświadczenie — do 4 miesięcy, a gdy wnioskodawca uzasadni potrzebę dokonania czynności prawnej — do 30 dni (art. 4 ustawy z 20 lipca 2018 r.). Sprzedaż — procedura cywilnoprawna (wycena, rokowania, umowa notarialna).",
  variantField: "rodzajGruntu",
  variants: {
    mieszkaniowy: { title: "Wniosek w sprawie przekształcenia prawa użytkowania wieczystego gruntu zabudowanego na cele mieszkaniowe" },
    inny: { title: "Wniosek o sprzedaż nieruchomości gruntowej na rzecz użytkownika wieczystego" },
  },
  sections: [
    {
      code: "grunt",
      title: "Rodzaj gruntu",
      fields: [
        {
          code: "rodzajGruntu",
          type: "radio",
          label: "Grunt oddany w użytkowanie wieczyste jest",
          required: true,
          options: [
            {
              value: "mieszkaniowy",
              label:
                "zabudowany na cele mieszkaniowe (dom jednorodzinny, budynek wielolokalowy, lokal w takim budynku) — użytkowanie wieczyste przekształciło się w prawo własności z mocy prawa",
            },
            { value: "inny", label: "inny (np. zabudowany na cele usługowe lub produkcyjne) — wnoszę o sprzedaż gruntu użytkownikowi wieczystemu" },
          ],
        },
        {
          code: "infoGrunt",
          type: "info",
          label:
            "Grunty zabudowane na cele mieszkaniowe przekształciły się w prawo własności 1 stycznia 2019 r. (albo z dniem oddania budynku do użytkowania) — urząd wydaje zaświadczenie i pobiera opłatę przekształceniową. Roszczenie o sprzedaż innych gruntów (art. 198g ustawy o gospodarce nieruchomościami) wygasło 31 sierpnia 2024 r.; sprzedaż jest możliwa na zasadach ogólnych, za zgodą gminy. Dla gruntów Skarbu Państwa wniosek składa się do starosty.",
        },
      ],
    },
    applicantSection(),
    representationSection(),
    {
      code: "zakres",
      title: "Zakres sprawy (grunt mieszkaniowy)",
      visibleWhen: RESIDENTIAL,
      fields: [
        {
          code: "zakresSprawy",
          type: "checkboxes",
          label: "Wnoszę o / zgłaszam",
          required: true,
          options: [
            { value: "zaswiadczenie", label: "wydanie zaświadczenia potwierdzającego przekształcenie prawa użytkowania wieczystego w prawo własności (art. 4 ustawy)" },
            { value: "oplataJednorazowa", label: "zamiar wniesienia jednorazowej opłaty przekształceniowej (art. 7 ust. 7 ustawy)" },
          ],
        },
        {
          code: "tryb30Dni",
          type: "checkbox",
          label: "Zaświadczenie jest mi potrzebne do dokonania czynności prawnej (np. sprzedaży lokalu) — wnoszę o wydanie go w terminie 30 dni",
          visibleWhen: CERTIFICATE,
        },
        { code: "uzasadnienie30Dni", type: "textarea", label: "Uzasadnienie potrzeby dokonania czynności prawnej", required: true, visibleWhen: URGENT, maxLength: 2000 },
        {
          code: "kwotaOplaty",
          type: "fee",
          label: "Opłata skarbowa za zaświadczenie",
          feeAmount: 50,
          visibleWhen: CERTIFICATE,
          hint: "50 zł — dołącz potwierdzenie przelewu na rachunek urzędu.",
        },
        { code: "bonifikata", type: "radio", label: "Czy wnosisz o bonifikatę od opłaty jednorazowej?", required: true, visibleWhen: ONE_OFF_FEE, options: YES_NO },
        {
          code: "bonifikataPodstawa",
          type: "textarea",
          label: "Podstawa bonifikaty",
          hint: "Wskaż przepis uchwały rady gminy w sprawie bonifikat, z którego wynika prawo do bonifikaty.",
          required: true,
          visibleWhen: DISCOUNT,
          maxLength: 1000,
        },
      ],
    },
    {
      code: "nieruchomosc",
      title: "Nieruchomość",
      fields: [
        { code: "nieruchomoscAdres", type: "text", label: "Położenie nieruchomości — miejscowość, ulica, numer", required: true, maxLength: 300 },
        parcelsField(),
        { code: "powierzchnia", type: "number", label: "Powierzchnia działki (działek) [m²]", width: "half", decimals: 2, min: 1 },
        { code: "kwGruntu", type: "text", label: "Numer księgi wieczystej gruntu", width: "half", maxLength: 30, placeholder: "np. KA1T/00012345/6" },
        { code: "udzialLokal", type: "checkbox", label: "Wniosek dotyczy udziału w gruncie związanego z własnością lokalu", visibleWhen: RESIDENTIAL },
        {
          code: "udzial",
          type: "text",
          label: "Wielkość udziału w prawie użytkowania wieczystego",
          required: true,
          visibleWhen: SHARE_IN_LAND,
          width: "half",
          maxLength: 50,
          placeholder: "np. 1234/100000",
        },
        { code: "kwLokalu", type: "text", label: "Numer księgi wieczystej lokalu", required: true, visibleWhen: SHARE_IN_LAND, width: "half", maxLength: 30 },
        { code: "sposobZabudowy", type: "text", label: "Sposób zabudowy i wykorzystania nieruchomości", required: true, visibleWhen: SALE, maxLength: 500 },
        {
          code: "dzialalnosc",
          type: "radio",
          label:
            "Czy grunt albo budynek lub lokal na nim położony jest wykorzystywany do prowadzenia działalności gospodarczej (także najmu lub dzierżawy)?",
          required: true,
          visibleWhen: SALE,
          options: YES_NO,
        },
        {
          code: "oswiadczenieKoszty",
          type: "declaration",
          label: "Oświadczam, że pokryję koszty związane ze sprzedażą (wycena nieruchomości, umowa notarialna, wpisy w księdze wieczystej).",
          required: true,
          visibleWhen: SALE,
        },
      ],
    },
    {
      code: "uzytkownicy",
      title: "Użytkownicy wieczyści (właściciele)",
      description: "Dane potrzebne do zaświadczenia albo umowy oraz wpisu w księdze wieczystej.",
      fields: [
        { code: "imionaRodzicow", type: "text", label: "Imiona rodziców wnioskodawcy", required: true, width: "half", maxLength: 200 },
        { code: "obywatelstwo", type: "text", label: "Obywatelstwo", required: true, width: "half", maxLength: 100, defaultValue: "polskie" },
        { code: "wspoluzytkownicy", type: "radio", label: "Czy nieruchomość ma innych współużytkowników wieczystych (współwłaścicieli)?", required: true, options: YES_NO },
        {
          code: "wspoluzytkownicyLista",
          type: "repeat",
          label: "Pozostali współużytkownicy wieczyści",
          itemLabel: "Współużytkownik",
          required: true,
          visibleWhen: CO_OWNERS,
          min: 1,
          max: 20,
          fields: [
            { code: "nazwa", type: "text", label: "Imię i nazwisko albo nazwa", required: true, width: "half", maxLength: 300 },
            { code: "identyfikator", type: "text", label: "PESEL (osoba fizyczna) albo NIP i KRS", required: true, width: "half", maxLength: 50 },
            { code: "imionaRodzicow", type: "text", label: "Imiona rodziców (osoba fizyczna)", width: "half", maxLength: 200 },
            { code: "adres", type: "text", label: "Adres albo siedziba", required: true, width: "half", maxLength: 300 },
          ],
        },
      ],
    },
    replySection(),
  ],
  attachments: [
    feeProofAttachment("Dowód zapłaty opłaty skarbowej za zaświadczenie (50 zł)", { visibleWhen: CERTIFICATE }),
    {
      code: "dokumentPilnosci",
      label: "Dokument uzasadniający potrzebę dokonania czynności prawnej (np. umowa przedwstępna)",
      hint: "Nieobowiązkowo — uzasadnienie wystarczy w treści wniosku.",
      visibleWhen: URGENT,
    },
    {
      code: "bonifikataDokumenty",
      label: "Dokumenty potwierdzające prawo do bonifikaty",
      hint: "Np. orzeczenie o niepełnosprawności, Karta Dużej Rodziny — zgodnie z uchwałą rady gminy.",
      visibleWhen: DISCOUNT,
      required: true,
      multiple: true,
    },
    {
      code: "umowaUzytkowania",
      label: "Umowa (akt notarialny) albo decyzja o oddaniu nieruchomości w użytkowanie wieczyste",
      visibleWhen: SALE,
      required: true,
    },
    {
      code: "podpisyWspoluzytkownikow",
      label: "Wniosek albo oświadczenie o przystąpieniu do wniosku podpisane przez pozostałych współużytkowników wieczystych",
      hint: "Przy sprzedaży wniosek składają wszyscy współużytkownicy wieczyści.",
      visibleWhen: CO_OWNERS,
      requiredWhen: SALE,
    },
    {
      code: "odpisKw",
      label: "Odpis księgi wieczystej gruntu (i lokalu)",
      hint: "Nieobowiązkowo — część urzędów prosi o aktualny odpis (nie starszy niż 3 miesiące).",
    },
    {
      code: "nastepstwoPrawne",
      label: "Dokumenty potwierdzające następstwo prawne (umowa, postanowienie o stwierdzeniu nabycia spadku, akt poświadczenia dziedziczenia)",
      hint: "Gdy stan prawny w księdze wieczystej jest niezgodny ze stanem faktycznym.",
      multiple: true,
    },
    PROXY_DOCUMENT,
    proxyFeeAttachmentWhen(RESIDENTIAL),
    otherAttachments(),
  ],
};

export default A12;
